import { MdAttachMoney, MdOutlineMultilineChart } from "react-icons/md";
import { SiGooglenews } from "react-icons/si";
import {
  GiBowman,
  GiCaveman,
  GiCoffeeCup,
  GiPayMoney,
  GiReceiveMoney,
} from "react-icons/gi";
import { TiCoffee } from "react-icons/ti";

/**
 * This function receives data from moving averages and extra evaluations,
 * organizes it into objects that are easy to render
 * @param {Object} movingAverageData
 * @param {Object} newsData
 * @returns {JSX.Element}
 */
export default function getOrganizedData(data, report) {
  const { movingAverageData, newsData, averagePrice: averagePriceData } = data;

  let chartBuyGroup = {
    name: "Compra",
    value: 0,
  };
  let chartSellGroup = {
    name: "Venda",
    value: 0,
  };
  let chartNeutralGroup = {
    name: "Imparcial",
    value: 0,
  };

  const addSliceToGroup = (groupName) => {
    switch (groupName) {
      case "BUY":
        return (chartBuyGroup.value = chartBuyGroup.value + 25);

      case "SELL":
        return (chartSellGroup.value = chartSellGroup.value + 25);

      default:
        return (chartNeutralGroup.value = chartNeutralGroup.value + 25);
    }
  };

  const colors = {
    good: "#66bb6a",
    bad: "#ef5350",
    neutral: "#757575",
  };
  let results = [];

  //Price Average Avaliations
  if (averagePriceData) {
    let { avaliation, averagePrice } = averagePriceData;
    switch (avaliation) {
      case 1:
        results.push({
          title: "Preço",
          subtitle: "Abaixo da média",
          description: `O preço deste ativo está abaixo de sua média de ${averagePrice}, talvez seja uma boa hora para comprar.`,
          color: colors.good,
          icon: <GiPayMoney />,
        });

        addSliceToGroup("BUY");
        break;
      case 2:
        results.push({
          title: "Preço",
          subtitle: "Acima da média",
          description: `O preço deste ativo está acima de sua média de ${averagePrice}, se você deseja comprar, talvez seja melhor esperar um preço mais justo.`,
          color: colors.bad,
          icon: <TiCoffee />,
        });

        addSliceToGroup("SELL");
        break;
      case 3:
        results.push({
          title: "Preço",
          subtitle: "Muito abaixo da média",
          description: `O preço deste ativo está muito abaixo de sua média de ${averagePrice}, se você deseja comprar, talvez seja uma boa hora.`,
          color: colors.good,
          icon: <GiPayMoney />,
        });
        addSliceToGroup("BUY");
        break;
      case 4:
        results.push({
          title: "Preço alto",
          subtitle: `"É de ouro?"`,
          description: `O preço deste ativo está muito acima de sua média de ${averagePrice}, se você deseja comprar, talvez não seja uma boa hora.`,
          color: colors.bad,
          icon: <GiReceiveMoney />,
        });
        addSliceToGroup("SELL");
        break;
      default:
        results.push({
          title: "Preço Médio",
          subtitle: "Neutro",
          description:
            "Ao analisar o preço, Lucy se manteve neutra sobre o preço deste ativo.",
          color: colors.neutral,
          icon: <MdAttachMoney />,
        });
        addSliceToGroup("NEUTRAL");
        break;
    }
  }

  // Market sentiment
  if (report) {
    const buyPoints = report.sentiment_buy;
    const sellPoints = report.sentiment_sell;

    if (buyPoints > sellPoints) {
      results.push({
        title: "Sentimento do Mercado",
        subtitle: "Otimista",
        description:
          "Ao analisar o sentimento de outros investidores sobre este ativo, foi possível destacar uma boa parcela deles otimistas sobre este ativo.",
        color: colors.good,
        icon: <GiBowman />,
      });

      addSliceToGroup("BUY");
    } else if (sellPoints > buyPoints) {
      results.push({
        title: "Sentimento do Mercado",
        subtitle: "Pessimista",
        description:
          "Ao analisar o sentimento de outros investidores sobre este ativo, foi possível destacar uma boa parcela deles pessimistas sobre este ativo.",
        color: colors.bad,
        icon: <GiCaveman />,
      });

      addSliceToGroup("SELL");
    } else {
      results.push({
        title: "Sentimento do Mercado",
        subtitle: "Imparcial",
        description:
          "Ao analisar o sentimento de outros investidores sobre este ativo, foi possível destacar uma boa parcela deles imparciais sobre este ativo.",
        color: colors.neutral,
        icon: <GiCoffeeCup />,
      });

      addSliceToGroup("NEUTRAL");
    }
  } else {
    results.push({
      title: "Sentimento do Mercado",
      subtitle: "Imparcial",
      description:
        "Ao analisar o sentimento de outros investidores sobre este ativo, foi possível destacar uma boa parcela deles imparciais sobre este ativo.",
      color: colors.neutral,
      icon: <GiCoffeeCup />,
    });

    addSliceToGroup("NEUTRAL");
  }

  //News Data Avaliations
  if (newsData || report) {
    let newsSentiment = 0;
    if (report) {
      newsSentiment = report.sentiment_sell;
    } else if (newsData) {
      newsSentiment = newsData.sentiment;
    }

    switch (newsSentiment) {
      case 1: //Buy
        results.push({
          title: "Boas",
          subtitle: "Notícias",
          description:
            "Ao analisar as notícias, foi possível destacar uma boa parte delas em contexto de alta sobre este ativo.",
          color: colors.good,
          icon: <SiGooglenews />,
        });

        addSliceToGroup("BUY");
        break;
      case 2: //Sell
        results.push({
          title: "Que deselegante!",
          subtitle: "Péssimas notícias",
          description:
            "Ao analisar as notícias, foi possível destacar uma boa parte delas em contexto de baixa sobre este ativo.",
          color: colors.bad,
          icon: <SiGooglenews />,
        });

        addSliceToGroup("SELL");
        break;

      default:
        //Neutral
        results.push({
          title: "Notícias",
          subtitle: "Monótonas",
          description:
            "Ao analisar as notícias, foi possível observar que os noticiários estão um pouco monótonos sobre este ativo.",
          color: colors.neutral,
          icon: <SiGooglenews />,
        });

        addSliceToGroup("NEUTRAL");
        break;
    }
  }

  //Moving Average Avaliations.
  if (
    movingAverageData &&
    movingAverageData !== "error" &&
    movingAverageData !== "empty"
  ) {
    const MMIndicationData = movingAverageData.specificInvestorType;
    switch (MMIndicationData.value) {
      case 1: //Buy
        results.push({
          title: "Médias Móveis",
          subtitle: "Indicando compra",
          description:
            "As médias móveis selecionadas para seu perfil de investidor estão, em sua grande maioria, indicando uma possível alta deste ativo.",
          color: colors.good,
          icon: <MdOutlineMultilineChart />,
        });
        addSliceToGroup("BUY");
        break;

      case 2: //Sell
        results.push({
          title: "Médias Móveis",
          subtitle: "Indicando venda",
          description:
            "As médias móveis selecionadas para seu perfil de investidor estão, em sua grande maioria, indicando uma possível queda deste ativo.",
          color: colors.bad,
          icon: <MdOutlineMultilineChart />,
        });
        addSliceToGroup("SELL");
        break;

      default:
        //Neutral
        results.push({
          title: "Médias Móveis",
          subtitle: "Imparcial",
          description:
            "As médias móveis selecionadas para seu perfil de investidor estão, em sua grande maioria, imparciais, é melhor esperar mais um pouco antes da tomada de decisão.",
          color: colors.neutral,
          icon: <MdOutlineMultilineChart />,
        });
        addSliceToGroup("NEUTRAL");
        break;
    }
  }

  if (results.length > 0) {
    return {
      indicators: results,
      chartData: [chartBuyGroup, chartSellGroup, chartNeutralGroup],
    };
  } else {
    return false;
  }
}
